import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import type { Role } from '@pixerito/api-client';
import { useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { Loading } from './components/ui';
import { LoginPage } from './pages/Login';
import { ClientDashboardPage } from './pages/client/ClientDashboard';
import { ClientDeliverablesPage } from './pages/client/ClientDeliverables';
import { ClientsPage } from './pages/admin/Clients';
import { NewClientPage } from './pages/admin/NewClient';
import { ClientDetailPage } from './pages/admin/ClientDetail';
import { AccountPage } from './pages/Account';

/**
 * Só deixa passar quem está logado e com o papel certo. O servidor também confere: isto é só navegação.
 * Quem entrou com uma senha provisória vai primeiro para "Minha conta" criar a própria.
 */
function RequireRole({ role, children }: { role?: Role; children: ReactNode }) {
  const { me, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (me.mustChangePassword && location.pathname !== '/conta') return <Navigate to="/conta" replace />;
  if (role && me.role !== role) return <Navigate to={me.role === 'admin' ? '/admin' : '/painel'} replace />;
  return <>{children}</>;
}

function Home() {
  const { me, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  return <Navigate to={me.role === 'admin' ? '/admin' : '/painel'} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Home />} />

      <Route
        element={
          <RequireRole role="client">
            <AppShell />
          </RequireRole>
        }
      >
        <Route path="/painel" element={<ClientDashboardPage />} />
        <Route path="/painel/entregaveis" element={<ClientDeliverablesPage />} />
      </Route>

      <Route
        element={
          <RequireRole role="admin">
            <AppShell />
          </RequireRole>
        }
      >
        <Route path="/admin" element={<ClientsPage />} />
        <Route path="/admin/clientes/novo" element={<NewClientPage />} />
        <Route path="/admin/clientes/:id" element={<ClientDetailPage />} />
        <Route path="/admin/clientes/:id/:tab" element={<ClientDetailPage />} />
      </Route>

      <Route
        element={
          <RequireRole>
            <AppShell />
          </RequireRole>
        }
      >
        <Route path="/conta" element={<AccountPage />} />
      </Route>

      <Route path="*" element={<Home />} />
    </Routes>
  );
}
